import { forwardRef, useCallback, useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import type { MediaFit } from "@gooner/shared";
import { isSafeMode } from "../../lib/safe-mode";
import { cx } from "./primitives";

/**
 * The one media element every "display card" shares: a `<video>` or `<img>` that
 * fills its parent with the right `object-fit` and — for video — the autoplay-with-
 * muted-fallback that used to be copy-pasted verbatim in RegionPlayer and GoonFuel.
 *
 * It renders only the element; callers own the positioned/aspect box and any
 * overlays. Forwarded refs land on the `<video>` (callers that need to pause /
 * mute / seek it imperatively).
 *
 * Two escape hatches keep every caller's behavior intact:
 *   • `canAutoplay()` — return false to skip kicking off playback (e.g. paused).
 *   • `onAutoMuted()`  — fired when unmuted autoplay was blocked and we muted to
 *                        recover, so a caller can sync its own muted state.
 */
interface MediaSurfaceProps {
  kind: "video" | "image";
  src: string;
  poster?: string;
  /** object-fit; defaults to "cover". */
  fit?: MediaFit;
  muted?: boolean;
  loop?: boolean;
  autoPlay?: boolean;
  className?: string;
  style?: CSSProperties;
  onEnded?: () => void;
  onError?: () => void;
  /** Fired once the element has enough to play through — the signal a caller waiting on
   *  a file that may not exist yet uses to swap out its pending state. */
  onCanPlayThrough?: () => void;
  canAutoplay?: () => boolean;
  onAutoMuted?: () => void;
  /** Images only: `loading="lazy" decoding="async"`. Opt-in — the wall and the players
   *  render what is on screen by definition; a scrolling list (the Feed) asks for it. */
  lazy?: boolean;
}

/**
 * The teardown itself, pulled out of the effect so it can be exercised directly — with a
 * plain fake object, no DOM, no React commit — against the exact ordering contract it
 * depends on. See tools/checks/media-release.mts, which imports this function and drives
 * it through both orderings below rather than grepping for it.
 *
 * `el` is a value captured ONCE, by the caller, at effect-setup time — never re-read from
 * a ref inside here. That is load-bearing, not style: on a genuine unmount, React (18.3.1,
 * confirmed against the copy in this repo's node_modules) detaches a host component's ref
 * — `safelyDetachRef`, called from `commitDeletionEffectsOnFiber`'s HostComponent case —
 * and removes its DOM node, during the synchronous mutation phase of the commit. Passive
 * effects (`useEffect` cleanups, including this one's caller) for that same deletion are
 * *always* flushed later, in a separately scheduled pass, strictly after that mutation
 * phase has finished. So a version of this function that re-read a ref instead of using a
 * value closed over at setup time would always see it already nulled by the time it ran on
 * a real unmount, and the release calls below would be unreachable dead code — which is
 * exactly the bug this shape fixes. Closing over the value once sidesteps that: the
 * reference stays valid even after the ref that used to point to it is cleared.
 *
 * `isConnected` is what tells a genuine unmount apart from React 18 StrictMode's synthetic
 * double-invoke (mount → cleanup → mount, dev only, no fiber deleted, nothing detached —
 * `commitDoubleInvokeEffectsInDEV` only re-runs effect lists, it never calls
 * `safelyDetachRef` or removes anything from the document). At the moment *that* cleanup
 * runs, the closed-over element is still attached (`isConnected === true`) because nothing
 * was ever deleted — so the guard below skips it. At the moment a *real* unmount's deferred
 * passive cleanup runs, the mutation phase's `removeChild` has already detached the node
 * from the document, so `isConnected` is `false` — so the guard lets it through. Do not
 * "fix" this by switching to `useLayoutEffect` to dodge the ref-nulling problem without
 * re-deriving the guard: a layout effect's destroy for a deleted ForwardRef fiber runs
 * *before* the recursive step that detaches its child's ref and removes its DOM node (per
 * the same `commitDeletionEffectsOnFiber`), so at that point the element is still attached
 * in BOTH orderings and `isConnected` no longer discriminates anything.
 */
export function releaseOnUnmount(el: HTMLVideoElement | null): () => void {
  return () => {
    if (!el || el.isConnected) return;
    el.pause();
    el.removeAttribute("src");
    el.load();
  };
}

export const MediaSurface = forwardRef<HTMLVideoElement, MediaSurfaceProps>(function MediaSurface(
  {
    kind,
    src,
    poster,
    fit = "cover",
    muted = true,
    loop = false,
    autoPlay = true,
    className,
    style,
    onEnded,
    onError,
    onCanPlayThrough,
    canAutoplay,
    onAutoMuted,
    lazy = false,
  },
  ref,
) {
  /**
   * Our own handle on the element, so the unmount cleanup can reach it whether or not a
   * caller passed a ref. Merged with the forwarded one rather than replacing it —
   * RegionPlayer drives play/pause/seek through that ref and must keep doing so.
   */
  const innerRef = useRef<HTMLVideoElement | null>(null);
  const setRef = useCallback(
    (el: HTMLVideoElement | null) => {
      innerRef.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref) (ref as { current: HTMLVideoElement | null }).current = el;
    },
    [ref],
  );

  /**
   * Release the element on the way out — see `releaseOnUnmount` above for why the value
   * has to be captured here, in the setup, and not re-read from `innerRef.current` inside
   * the cleanup.
   *
   * Unmounting a <video> detaches it but does not stop it: it keeps its decode buffer,
   * and on a range-served source it keeps requesting bytes, for as long as it takes the
   * GC to notice. On a screen that mounts thirty of them at once — the view wall — that
   * is the difference between "navigating away stops the media" and "navigating away
   * leaves thirty streams running behind the next page". pause() alone is not enough;
   * the pending range request survives it. Clearing `src` and calling load() is what
   * aborts it.
   *
   * Deliberately empty deps: this must run when the element goes away, not when a prop
   * changes. Every caller keys this component on its src, so a source change is a
   * remount and the element this closes over is the one being discarded either way —
   * except `ClipViewer`, which swaps `src` on one persistent instance for Prev/Next, so
   * this cleanup correctly never fires there; it fires only when the viewer itself closes.
   */
  useEffect(() => releaseOnUnmount(innerRef.current), []);

  const cls = cx("h-full w-full", fit === "contain" ? "object-contain" : "object-cover", className);
  // No object-position override → CSS default "50% 50%" = plain center-cover.
  const merged: CSSProperties | undefined = style;

  if (kind === "image") {
    return lazy ? (
      <img src={src} alt="" draggable={false} className={cls} style={merged} loading="lazy" decoding="async" />
    ) : (
      <img src={src} alt="" draggable={false} className={cls} style={merged} />
    );
  }

  return (
    <video
      ref={setRef}
      src={src}
      poster={poster}
      className={cls}
      style={merged}
      autoPlay={autoPlay}
      loop={loop}
      muted={muted}
      playsInline
      controls={false}
      // Picture-in-Picture composites decoded frames on an OS surface that CSS
      // filters never reach, so it would pop a safe-mode clip out fully clear.
      disablePictureInPicture={isSafeMode()}
      onEnded={onEnded}
      onError={onError}
      onCanPlayThrough={onCanPlayThrough}
      onCanPlay={(e) => {
        if (autoPlay === false) return;
        if (canAutoplay && !canAutoplay()) return;
        const v = e.currentTarget;
        v.play().catch(() => {
          // Unmuted autoplay was blocked — fall back to muted playback.
          if (!v.muted) {
            v.muted = true;
            onAutoMuted?.();
            v.play().catch(() => undefined);
          }
        });
      }}
    />
  );
});
